import React from "react";
import { Columns, Plus, Trash2 } from "lucide-react";
import {
  ColumnDef,
  getLineTotalFormulaColumn,
} from "../../lib/quote-invoice-columns";
import { MobileFlowCard } from "./mobile-flow-layout";
import { cn } from "../../lib/utils";

interface MobileLineItemsEditorProps<T> {
  title: string;
  items: T[];
  columns: ColumnDef[];
  columnVisibility: Record<string, boolean>;
  getItemKey: (item: T, index: number) => React.Key;
  /** Editable control for an input column (text, number or select). */
  renderField: (column: ColumnDef, item: T, index: number) => React.ReactNode;
  getFormulaValue: (column: ColumnDef, item: T, index: number) => number;
  onAdd: () => void;
  onRemove: (index: number) => void;
  onConfigureColumns: () => void;
  currency: string;
  subTotal: number;
  vat: number;
  total: number;
}

const formatValue = (value: number) =>
  Number.isFinite(value) ? value.toLocaleString() : "";

/** Quote/invoice line items as stacked cards instead of a wide, resizable table. */
function MobileLineItemsEditor<T>({
  title,
  items,
  columns,
  columnVisibility,
  getItemKey,
  renderField,
  getFormulaValue,
  onAdd,
  onRemove,
  onConfigureColumns,
  currency,
  subTotal,
  vat,
  total,
}: MobileLineItemsEditorProps<T>) {
  const visibleColumns = columns.filter(
    (column) => columnVisibility[column.id] !== false,
  );
  const descriptionColumn = visibleColumns.find(
    (column) => column.id === "description",
  );
  const inputColumns = visibleColumns.filter(
    (column) =>
      column.id !== "description" &&
      (column.type === "text" ||
        column.type === "number" ||
        column.type === "select"),
  );
  const formulaColumns = visibleColumns.filter(
    (column) => column.type === "formula" && column.formula,
  );
  const lineTotalId = getLineTotalFormulaColumn(columns)?.id;

  return (
    <>
      <MobileFlowCard
        title={title}
        description={`${items.length} ${items.length === 1 ? "item" : "items"}`}
        action={
          <button
            type="button"
            onClick={onConfigureColumns}
            className="flex h-9 items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-700 active:bg-gray-50"
          >
            <Columns className="h-4 w-4" />
            Columns
          </button>
        }
      >
        {items.length === 0 && (
          <p className="rounded-xl border border-dashed border-gray-200 px-4 py-6 text-center text-sm text-gray-500">
            No items yet. Add your first line item below.
          </p>
        )}
      </MobileFlowCard>

      {items.map((item, index) => (
        <MobileFlowCard
          key={getItemKey(item, index)}
          title={`Item ${index + 1}`}
          action={
            <button
              type="button"
              aria-label={`Remove item ${index + 1}`}
              onClick={() => onRemove(index)}
              className="flex h-9 w-9 items-center justify-center rounded-xl border-0 bg-red-50 p-0 text-red-600 active:bg-red-100"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          }
        >
          <div className="space-y-3">
            {descriptionColumn && (
              <div className="space-y-1.5">
                <span className="block text-[13px] font-medium text-gray-700">
                  {descriptionColumn.label}
                </span>
                {renderField(descriptionColumn, item, index)}
              </div>
            )}
            {inputColumns.length > 0 && (
              <div className="grid grid-cols-2 gap-3">
                {inputColumns.map((column) => (
                  <div key={column.id} className="min-w-0 space-y-1.5">
                    <span className="block truncate text-[13px] font-medium text-gray-700">
                      {column.label}
                    </span>
                    {renderField(column, item, index)}
                  </div>
                ))}
              </div>
            )}
            {formulaColumns.length > 0 && (
              <dl className="space-y-1.5 rounded-xl bg-gray-50 px-3 py-2.5">
                {formulaColumns.map((column) => {
                  const isLineTotal = column.id === lineTotalId;
                  return (
                    <div
                      key={column.id}
                      className="flex items-center justify-between gap-3"
                    >
                      <dt
                        className={cn(
                          "truncate text-xs",
                          isLineTotal
                            ? "font-semibold text-gray-900"
                            : "text-gray-500",
                        )}
                      >
                        {column.label}
                      </dt>
                      <dd
                        className={cn(
                          "shrink-0 tabular-nums",
                          isLineTotal
                            ? "text-sm font-bold text-gray-900"
                            : "text-xs text-gray-700",
                        )}
                      >
                        {isLineTotal && `${currency} `}
                        {formatValue(getFormulaValue(column, item, index))}
                      </dd>
                    </div>
                  );
                })}
              </dl>
            )}
          </div>
        </MobileFlowCard>
      ))}

      <button
        type="button"
        onClick={onAdd}
        className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-primary-300 bg-primary-50 text-sm font-semibold text-primary-700 active:bg-primary-100"
      >
        <Plus className="h-4 w-4" />
        Add item
      </button>

      <MobileFlowCard>
        <dl className="space-y-2 text-sm">
          <div className="flex justify-between gap-3">
            <dt className="text-gray-500">Sub Total</dt>
            <dd className="font-semibold tabular-nums text-gray-900">
              {currency} {subTotal.toLocaleString()}
            </dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt className="text-gray-500">VAT</dt>
            <dd className="font-semibold tabular-nums text-gray-900">
              {currency} {vat.toLocaleString()}
            </dd>
          </div>
          <div className="flex justify-between gap-3 border-t border-gray-100 pt-2">
            <dt className="font-semibold text-gray-900">Total</dt>
            <dd className="text-base font-bold tabular-nums text-gray-900">
              {currency} {total.toLocaleString()}
            </dd>
          </div>
        </dl>
      </MobileFlowCard>
    </>
  );
}

export default MobileLineItemsEditor;
